import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { get, invoke } from 'lodash';
import { useApp } from '../../context/app-context';
import Modal from '../../common/Modal/Modal';
import { css } from '@emotion/css';
import moment from 'moment';
import skipOrder from '../../service/skip-order-service';
import { ItemsContext, ItemsContextType } from './ItemsContext';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';

const skipOrderStyles = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
`;

const buttonContainer = css`
  display: flex;
  justify-content: center;
`;

const confirmButtonStyle = css`
  background-color: #7b1a27;
  color: #fff;
  border: 0;
  border-radius: 6.25rem;
  padding: 1rem 0.75rem;
  min-width: 14rem;
  cursor: pointer;
`;

const SkipOrderLink = (props) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);

  const { className, text = 'Skip this order', orderDate, nextOrderDate, order } = props;

  const dateFormat = 'ddd MMM DD';
  const currentDate = moment(orderDate, 'YYYY-M-D');
  const nextDate = moment(nextOrderDate, 'YYYY-M-D');

  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, showToast, publish } = useApp();
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;

  const desc = `Your next order for ${currentDate.format(dateFormat)} will be skipped. Your next Schedule & Save order is scheduled for ${nextDate.format(dateFormat)}`;

  const showModal = () => {
    if (currentDate.isValid() && nextDate.isValid()) {
      setModal(true);
      analyticsEvents('view', 'modalView');
    }
  };

  const postOrderSkip = async () => {
    try {
      await invoke(currentSessionContext, 'ensureActiveSession', () =>
        skipOrder(appData.environmentVariables, user, {
          NextOrderId: get(order, 'snsNextOrderId'),
          orderDate: get(order, 'orderDate'),
        }),
      );

      showToast('info', `Your ${moment(orderDate, 'YYYY-M-D').format('MMM D')} order will be skipped.`);
      publish('skipOrder');
      await invoke(currentItemsContext, 'fetchItemsInfo');
      analyticsEvents('success', 'modalClick');
    } catch (err) {
      showToast('error', `We couldn't complete your skip item request. Please try again later.`);
      analyticsEvents('un-success', 'modalClick');
    }
  };

  const handleConfirm = async () => {
    setLoading(true);

    await postOrderSkip();

    setLoading(false);
    setModal(false);
  };

  const analyticsEvents = (name, eventType) => {
    try {
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.handleModalAndLinkEvents({
          eventType: eventType,
          modalAndLinkData: `ar:skip-order${window['digitalData']?.pageFlags?.isSnSItemsListExpanded ? '-expanded' : ''}|${name}`,
        });
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  return (
    <div className={className}>
      <button className={skipOrderStyles} onClick={showModal} aria-label="Skip this order button">
        <span className="link-sm">{text}</span>
      </button>

      <Modal titleText="Skip this order" description={desc} onClose={() => setModal(false)} show={modal} maxWidth={28}>
        <div className={buttonContainer}>
          <button
            className={`${confirmButtonStyle} body-text`}
            onClick={handleConfirm}
            disabled={loading}
            aria-label="Confirm order skip"
          >
            {loading ? '...' : 'Confirm'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

SkipOrderLink.propTypes = {
  text: PropTypes.string,
  orderDate: PropTypes.string.isRequired,
  nextOrderDate: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
};

export default SkipOrderLink;
